import React, { CSSProperties, useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import IndexedList from '../../support/shared/IndexedList';
import {
  applyInvoice,
  getAllPaymentLineItems,
  getTotals,
} from '../../support/shared/billing';
import { Addon } from '../../types/shared/Addon';
import formatCurrency from '../../core/support/formatCurrency';
import {
  VendorSummary,
  VendorInvoice,
  LineItem as LineItemType,
} from '../../types/shared/Vendor';
import { Text } from '../uiold';
import { Checkbox, TableContainer, Table, Tbody, Td, Tr } from '../../core-ui';
import { TableContainerProps } from '@chakra-ui/react';
import { LineItem } from './LineItem';
import { PaymentLineItem } from './PaymentLineItem';
import { AdjustmentLineItem } from './AdjustmentLineItem';

type Vendor = {
  id: string;
  name: string | null | undefined;
  billing?: VendorSummary['billing'];
  payments?: VendorSummary['payments'];
  created_at: string;
};

type Market = {
  id: string;
  name: string;
};

type Props = {
  vendor: Vendor;
  markets: Array<Market> | IndexedList<Market> | null | undefined;
  vendorInvoice?: VendorInvoice;
  currency: string;
  addons: Map<string, Addon> | null | undefined;
  allowSelection?: boolean;
  renderSelectionToolbar?: (props: {
    items: Array<LineItemType>;
    selectedItems: Set<LineItemType>;
    selectNone: () => void;
  }) => React.ReactChild;
  containerProps?: TableContainerProps;
  vendorInvoiceId?: string;
};

// TODO: Rename this to VendorBillingSummary?

function VendorInvoiceSummary(props: Props) {
  let {
    addons,
    allowSelection,
    currency,
    markets,
    renderSelectionToolbar,
    vendor,
    vendorInvoice,
    vendorInvoiceId,
    containerProps,
  } = props;

  const invoice = vendorInvoiceId
    ? vendor.billing?.invoices.find((invoice) => invoice.id === vendorInvoiceId)
    : undefined;

  let billing = applyInvoice(vendor.billing, vendorInvoice);

  // Determines if a line item should be shown
  const isVisible = (lineItem: LineItemType) => {
    if (lineItem.type === 'payment' || lineItem.type === 'adjustment') {
      return true;
    }
    if (lineItem.type === 'note') {
      return false;
    }
    if (lineItem.amount === 0 && !lineItem.dates?.length) {
      return false;
    }
    return true;
  };

  const billingSummaryLineItems = useMemo(() => {
    const paymentLineItems = getAllPaymentLineItems(
      vendor.billing?.invoices ?? [],
    );
    return [
      ...billing.invoicedItems.filter((item) => isVisible(item)),
      ...paymentLineItems,
    ];
  }, [billing.invoicedItems, vendor.billing?.invoices]);

  let {
    subTotal,
    taxTotal,
    grandTotal,
    amountPaid,
    remainingBalance,
  } = getTotals(invoice ? invoice.invoicedLineItems : billingSummaryLineItems);

  const visibleItems = useMemo(
    () =>
      invoice
        ? invoice.invoicedLineItems.filter((item) => isVisible(item))
        : billingSummaryLineItems,
    [billingSummaryLineItems, invoice],
  );

  const {
    selectedItems,
    isSelected,
    allSelected,
    someSelected,
    toggleSelection,
    selectNone,
    selectAll,
  } = useSelectionState(visibleItems);

  let dividingLineStyle: CSSProperties = {
    height: 3,
    padding: 0,
    backgroundColor: '#bbb',
  };

  const numberOfColumns = allowSelection ? 4 : 3;

  return (
    <TableContainer whiteSpace="normal" {...containerProps}>
      <Table variant="striped">
        <Tbody>
          {someSelected ? (
            <Tr>
              <Td style={checkboxCell}>
                <View style={styles.checkboxContainer}>
                  <Checkbox
                    isIndeterminate={!allSelected}
                    isChecked={true}
                    onChange={() => {
                      allSelected ? selectNone() : selectAll();
                    }}
                  />
                </View>
              </Td>
              <Td colSpan={3} style={{ padding: 0 }}>
                <View style={styles.headerContainer}>
                  {renderSelectionToolbar
                    ? renderSelectionToolbar({
                        items: visibleItems,
                        selectedItems,
                        selectNone,
                      })
                    : null}
                </View>
              </Td>
            </Tr>
          ) : (
            <Tr>
              <Td colSpan={numberOfColumns} style={{ padding: 0 }}>
                <View style={styles.headerContainer}>
                  <Text style={styles.headerText}>
                    {t('Charges and Adjustments')}
                  </Text>
                </View>
              </Td>
            </Tr>
          )}
          {visibleItems.map((lineItem, i) => {
            if (lineItem.type === 'adjustment') {
              return (
                <AdjustmentLineItem
                  key={i}
                  allowSelection={allowSelection}
                  currency={currency}
                  isSelected={isSelected}
                  lineItem={lineItem}
                  toggleSelection={toggleSelection}
                />
              );
            }
            if (lineItem.type === 'payment') {
              return (
                <PaymentLineItem
                  key={i}
                  allowSelection={allowSelection}
                  currency={currency}
                  isSelected={isSelected}
                  lineItem={lineItem}
                  toggleSelection={toggleSelection}
                />
              );
            }
            // TODO: Replace with lineItem.marketName
            let marketName =
              markets?.find((market) => market.id === lineItem.marketId)
                ?.name ?? '';
            return (
              <LineItem
                key={i}
                addons={addons}
                allowSelection={allowSelection}
                currency={currency}
                isSelected={isSelected}
                lineItem={lineItem}
                marketName={marketName}
                toggleSelection={toggleSelection}
              />
            );
          })}
          <Tr>
            <Td colSpan={numberOfColumns} style={dividingLineStyle} />
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns} style={dividingLineStyle} />
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns - 1} style={{ textAlign: 'right' }}>
              <Text>{t('Subtotal')}</Text>
            </Td>
            <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
              <Text>{formatCurrency(subTotal * 100, currency)}</Text>
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns - 1} style={{ textAlign: 'right' }}>
              <Text>{t('Taxes')}</Text>
            </Td>
            <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
              <Text>{formatCurrency(taxTotal * 100, currency)}</Text>
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns - 1} style={{ textAlign: 'right' }}>
              <Text style={styles.totals}>{t('Invoiced Total')}</Text>
            </Td>
            <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
              <Text>
                {isNaN(grandTotal)
                  ? t('Error')
                  : formatCurrency(grandTotal * 100, currency)}
              </Text>
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns - 1} style={{ textAlign: 'right' }}>
              <Text style={styles.totals}>{t('Amount Paid')}</Text>
            </Td>
            <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
              <Text>-{formatCurrency(amountPaid * 100, currency)}</Text>
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={numberOfColumns - 1} style={{ textAlign: 'right' }}>
              <Text style={styles.totals}>{t('Remaining Balance')}</Text>
            </Td>
            <Td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
              <Text
                style={
                  Number(remainingBalance.toFixed(2)) <= 0
                    ? { color: 'green' }
                    : { color: 'red' }
                }
              >
                {formatCurrency(Math.abs(remainingBalance * 100), currency)}{' '}
                {remainingBalance < 0 ? t('Credit') : ''}
              </Text>
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function useSelectionState<T extends object>(items: Array<T>) {
  const [selectedItems, setSelectedItems] = useState(new Set<T>());

  const isSelected = (item: T) => selectedItems.has(item);

  const allSelected = items.every((item) => isSelected(item));

  const someSelected = items.some((item) => isSelected(item));

  const toggleSelection = (item: T) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(item)) {
      newSelection.delete(item);
    } else {
      newSelection.add(item);
    }
    setSelectedItems(newSelection);
  };

  const selectNone = () => {
    setSelectedItems(new Set());
  };

  const selectAll = () => {
    setSelectedItems(new Set(items));
  };

  return {
    selectedItems,
    isSelected,
    allSelected,
    someSelected,
    toggleSelection,
    selectNone,
    selectAll,
  };
}

let styles = StyleSheet.create({
  headerContainer: {
    height: 46,
    justifyContent: 'center',
    paddingHorizontal: 11,
  },
  headerText: {
    color: '#1F4B99',
    fontWeight: 'bold',
    fontSize: 18,
  },
  checkboxCell: {
    padding: 0,
    minWidth: 20,
    maxWidth: 20,
    position: 'relative',
  },
  checkboxContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  totals: {
    fontWeight: '600',
  },
});

export const checkboxCell = StyleSheet.flatten(styles.checkboxCell);

export default VendorInvoiceSummary;
